// Ticket-group aggregation view. One Jira ticket fans out into N per-repo
// Runs grouped under a TicketGroup with a locked cross-repo contract. This
// page renders the locked-contract panel + the group status + one RunLane
// per affected repo. Polls the group via the shared poller and stops once
// the group settles (COMPLETED / PARTIAL / FAILED).
//
// Defensive by design: the SS shape lands in parallel, so every field is
// guarded. A group with a single lane renders like a single-repo run.

import { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import RunLane from "./RunLane";
import Shell from "./Shell";
import {
  ErrorState,
  GroupStatusBadge,
  Html,
  LoadingState,
  Markdown,
  RefreshButton,
  apiErrorMessage,
  formatMoney,
  isGroupActive
} from "./shared";

// The contract is server-trusted markdown/text from the architect. If the
// API tags it as HTML render via the sanitized Html component; otherwise
// render via the safe markdown renderer (same one used for plan/body).
function ContractPanel({ contract }) {
  if (!contract) return null;
  const isHtml =
    typeof contract === "object" && contract.kind === "html";
  const src =
    typeof contract === "string"
      ? contract
      : (contract && contract.content) || "";
  if (!src) return null;
  return (
    <div className="bss-card" style={{ padding: 0, marginBottom: 24 }}>
      <div
        className="bss-card-hd"
        style={{ display: "flex", alignItems: "center", gap: 10 }}
      >
        <Icon.FileText size={13} style={{ color: "var(--bss-boom-pink)" }} />
        <h3
          style={{
            fontSize: 13,
            color: "var(--bss-fg-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em"
          }}
        >
          Locked cross-repo contract
        </h3>
      </div>
      <div style={{ padding: "18px 20px 20px" }}>
        {isHtml ? <Html src={src} /> : <Markdown src={src} />}
      </div>
    </div>
  );
}

// Normalize the two possible entry points into one shape:
// - GET /groups/:id            → { id, key, status, contract, lanes, ... }
// - GET /tickets/:key.group    → { group: {...}, lanes: [...] }
function normalizeGroup(data) {
  if (!data) return null;
  const group = data.group || data;
  // A 404 / garbage / soft-200 body (e.g. `{}` or `{error:'not_found'}`)
  // has none of the group's identifying fields. Treat it as "no group" so
  // the caller renders ErrorState + retry instead of a stuck fake-PLANNING
  // shell that polls forever.
  const key = group.key || data.key;
  if (group.id == null && group.status == null && key == null) return null;
  const lanes = data.lanes || group.lanes || [];
  return {
    id: group.id,
    key,
    status: group.status,
    contract: group.contract,
    affectedRepos: group.affectedRepos || [],
    lanes
  };
}

export default function GroupDetailPage() {
  const { id } = useParams();
  const history = useHistory();
  const [toast, setToast] = useState(null);

  const fetchGroup = useCallback(
    () => api.bssGroup(id).then((res) => res.data),
    [id]
  );
  const { data, error, loading, refresh } = useBssPoll(fetchGroup);

  const group = useMemo(() => normalizeGroup(data), [data]);
  const lanes = useMemo(() => (group && group.lanes) || [], [group]);
  const totalSpend = useMemo(
    () =>
      lanes.reduce((s, l) => s + ((l.run && l.run.costUsd) || 0), 0),
    [lanes]
  );

  // Only block the whole page on the *initial* load. A post-deploy
  // refresh() flips loading=true with data still present — keep the page
  // (toast + lanes) mounted and let the inline/stale indicators cover the
  // refetch (mirrors RunsPage's `error && !data` gating).
  if (loading && !data) {
    return (
      <Shell>
        <LoadingState label="Loading ticket group…" />
      </Shell>
    );
  }
  if (error || !group) {
    return (
      <Shell>
        <ErrorState
          message={apiErrorMessage(error, "Ticket group not found.")}
          onRetry={refresh}
        />
      </Shell>
    );
  }

  const live = isGroupActive(group.status);

  return (
    <Shell>
      <div className="bss-crumb">
        <button
          type="button"
          className="bss-btn bss-btn--ghost bss-btn--sm"
          onClick={() => history.push("/bss/tickets")}
        >
          <Icon.ChevronLeft size={13} /> Tickets
        </button>
        <span>/</span>
        {group.key && (
          <>
            <button
              type="button"
              className="bss-btn bss-btn--ghost bss-btn--sm"
              onClick={() =>
                history.push(
                  `/bss/tickets/${encodeURIComponent(group.key)}`
                )
              }
            >
              <Icon.Ticket size={13} /> {group.key}
            </button>
            <span>/</span>
          </>
        )}
        <span style={{ fontFamily: "var(--bss-font-mono)" }}>
          group {group.id}
        </span>
        <span style={{ marginLeft: "auto" }}>
          <RefreshButton onClick={refresh} loading={loading} />
        </span>
      </div>

      <div className="bss-card" style={{ padding: 24, marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap"
          }}
        >
          <h1 style={{ fontSize: 22 }}>
            {group.key ? `${group.key} — ticket group` : "Ticket group"}
          </h1>
          <GroupStatusBadge status={group.status} pulsing={live} />
          <span
            style={{
              marginLeft: "auto",
              display: "flex",
              gap: 24,
              fontSize: 12.5,
              color: "var(--bss-fg-muted)"
            }}
          >
            <span>
              <strong style={{ color: "var(--bss-fg)" }}>
                {lanes.length}
              </strong>{" "}
              {lanes.length === 1 ? "repo" : "repos"}
            </span>
            <span>
              Total spend{" "}
              <strong
                style={{
                  color: "var(--bss-fg)",
                  fontFamily: "var(--bss-font-mono)"
                }}
              >
                {formatMoney(totalSpend)}
              </strong>
            </span>
          </span>
        </div>
        {group.affectedRepos.length > 0 && (
          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 6,
              flexWrap: "wrap"
            }}
          >
            {group.affectedRepos.map((r) => (
              <span key={r} className="bss-badge bss-badge--gray">
                <Icon.Github size={11} /> {r}
              </span>
            ))}
          </div>
        )}
      </div>

      <ContractPanel contract={group.contract} />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 14
        }}
      >
        <h2 className="bss-ticket-section-h">Per-repo lanes</h2>
        <span style={{ color: "var(--bss-fg-subtle)", fontSize: 12 }}>
          {lanes.length} {lanes.length === 1 ? "lane" : "lanes"}
        </span>
      </div>

      {lanes.length === 0 ? (
        <div className="bss-card" style={{ padding: 24 }}>
          <div style={{ color: "var(--bss-fg-muted)", fontSize: 13 }}>
            No repo lanes yet — the architect is still scoping this ticket.
          </div>
        </div>
      ) : (
        lanes.map((lane) => (
          <RunLane
            key={lane.repoId || (lane.run && lane.run.id) || lane.githubRepo}
            repoId={lane.repoId}
            githubRepo={lane.githubRepo}
            run={lane.run}
            prUrl={lane.prUrl}
            status={lane.status}
            deployWorkflows={lane.deployWorkflows}
            relatedRuns={lane.relatedRuns}
            ticketKey={group.key}
            onNavigateRun={(rid) =>
              history.push(`/bss/runs/${rid}`)
            }
            onDeployed={(res, env, repo) => {
              setToast(
                `Dispatched deploy → ${
                  repo && (repo.githubRepo || repo.repoId)
                } · ${env ? env.label : "environment"}`
              );
              window.setTimeout(() => setToast(null), 3500);
              refresh();
            }}
          />
        ))
      )}

      {toast && (
        <div className="bss-toast">
          <Icon.PlayCircle
            size={16}
            style={{ color: "var(--bss-boom-pink)" }}
          />
          {toast}
        </div>
      )}
    </Shell>
  );
}
